import * as THREE from "three";
import { BaseModify } from "../base/BaseModify";
import { Context } from "../core/context";
import { DBEntity, DbLine, DbMesh } from "@/CadEngine";
import {
  ModifyOperateState,
  DrawOperationMovePointBeforeParams,
  DrawOperationMovePointParams,
  IModifyOperation,
} from "./IModifyOperation";
import { ULabel } from "@/ui/ULabel";
import {
  CircleArcInfoUseFilleted,
  CommonColor,
  LinePoints,
  Linearity,
  SampleLayerType,
} from "@/core/Parameters";

class FilletedCornerModify implements IModifyOperation {
  private m_context: Context;
  private m_selects: DbLine[] = [];
  private m_label: ULabel;
  private m_prePoint: THREE.Vector3 = new THREE.Vector3();
  private m_curPoint: THREE.Vector3 = new THREE.Vector3();
  private m_tempLineObject!: THREE.LineSegments;
  private m_tempHeightLineObject!: THREE.LineSegments;
  private m_tempDarkLineObject!: THREE.LineSegments;
  private m_curSelect: DbLine | undefined;
  private m_radius: number = 0;
  private m_delLineInfos: DbLine[] = [];
  private m_addLineInfos: DbLine[] = [];
  private m_preLinePoints: THREE.Vector3[] = [];
  private m_curLinePoints: THREE.Vector3[] = [];
  private m_preClickLinePoints: THREE.Vector3[] | undefined;
  private m_curClickLinePoints: THREE.Vector3[] | undefined;
  private m_addLine1Points: THREE.Vector3[] = [];
  private m_addLine2Points: THREE.Vector3[] = [];
  private m_addLine3Points: THREE.Vector3[] = [];
  private m_circleAceInfo: CircleArcInfoUseFilleted | undefined = undefined;
  private m_circleArcOriginPoints: THREE.Vector3[] = [];
  private m_locationObject: THREE.Line;
  private m_firstSelectLayerLineTypeName: Linearity | undefined;
  private m_curSelectLayerLineTypeName: Linearity | undefined;
  private m_accordLayerNames: string[] = [];

  constructor(
    context: Context,
    radius: number = 0,
    accordLayerNames: string[] = []
  ) {
    this.m_context = context;
    this.m_label = this.m_context.ModifyManager.TextLabel;
    this.m_radius = radius;
    this.m_locationObject = context.ToolsManager.CreatedLocationLine();
    this.createTempLineObject();
    this.createTempHightLineObject();
    this.createTempDarkLineObject();
    this.m_accordLayerNames = accordLayerNames;
  }

  OnMovePointBefore(params: DrawOperationMovePointBeforeParams): void {}
  OnMovePoint(point: DrawOperationMovePointParams): void {
    let { obejct } = point;
    this.m_curSelect = undefined;
    this.m_context.SelectionManager.HighLighting = true;
    let textLabelPosition = point.point.clone();
    this.m_curPoint = point.point.clone();
    this.m_locationObject.position.copy(this.m_curPoint);
    if (obejct?.length) {
      this.m_curSelect = obejct[0] as DbLine;
    }
    textLabelPosition.x += 30 * this.m_context.SelectionManager.Scale;
    textLabelPosition.y -= 10 * this.m_context.SelectionManager.Scale;
    if (!this.m_selects.length) {
      this.m_label.SetVaule(
        "请选取第一条管线",
        textLabelPosition,
        new THREE.Vector3(-1, 0, 0)
      );
    } else if (this.m_selects.length == 1) {
      this.m_tempLineObject.geometry.setFromPoints([]);
      this.m_label.SetVaule(
        "请选取第二条管线",
        textLabelPosition,
        new THREE.Vector3(-1, 0, 0)
      );
      this.m_circleAceInfo = undefined;
      if (this.m_curSelect) {
        let estimateResult =
          this.m_context.ToolsManager.VerifyLayerNameOrDataType(
            this.m_curSelect,
            this.m_accordLayerNames
          );
        if (estimateResult) {
          this.m_curSelectLayerLineTypeName =
            this.m_context.ToolsManager.GetLayerLineTypeName(this.m_curSelect);
          let p = this.getObjPoints([...this.m_curSelect.points]);
          if (this.m_curSelectLayerLineTypeName == Linearity.Dash) {
            p = [
              new THREE.Vector3(
                this.m_curSelect.points[0],
                this.m_curSelect.points[1],
                this.m_curSelect.points[2]
              ),
              new THREE.Vector3(
                this.m_curSelect.points.at(-3),
                this.m_curSelect.points.at(-2),
                this.m_curSelect.points.at(-1)
              ),
            ];
            if (this.m_curSelect.userData?.circleArcInfo) {
              return;
            }
          }
          this.m_curLinePoints = [...p];
          this.m_curClickLinePoints = this.filterClickLine(
            this.m_curLinePoints,
            this.m_curPoint!
          );
          this.computeCrossPoint();

          this.m_tempDarkLineObject.geometry.setFromPoints([
            ...this.m_preLinePoints,
            ...this.m_curLinePoints,
          ]);
        }
      } else {
        this.m_tempDarkLineObject.geometry.setFromPoints([]);
      }
    }
    this.m_label.Visibility = true;
  }
  OnDownPoint(point: DrawOperationMovePointParams): void {
    let { obejct } = point;
    if (obejct?.length) {
      let obj = obejct[0] as DbLine;
      let estimateResult =
        this.m_context.ToolsManager.VerifyLayerNameOrDataType(
          obj,
          this.m_accordLayerNames
        );
      if (estimateResult) {
        if (this.m_selects.length == 1) {
          this.endChamferHandle();
        } else {
          this.m_prePoint.copy(point.point);
          this.m_selects.push(obj);
          this.m_firstSelectLayerLineTypeName =
            this.m_context.ToolsManager.GetLayerLineTypeName(obj);
          let p = this.getObjPoints([...obj.points]);
          if (this.m_firstSelectLayerLineTypeName == Linearity.Dash) {
            p = [
              new THREE.Vector3(obj.points[0], obj.points[1], obj.points[2]),
              new THREE.Vector3(
                obj.points.at(-3),
                obj.points.at(-2),
                obj.points.at(-1)
              ),
            ];
          }
          this.m_preLinePoints = [...p];
          this.m_preClickLinePoints = this.filterClickLine(
            this.m_preLinePoints,
            this.m_prePoint!
          );
          this.m_tempHeightLineObject.geometry.setFromPoints(
            this.m_preClickLinePoints!
          );
        }
      }
    }
  }

  onKeyDown(e: KeyboardEvent): void {}
  onKeyUp(e: KeyboardEvent): void {}

  OnZoom(camera: THREE.Camera): void {}
  OnWheel(e: WheelEvent): void {}

  onMouseMove(e: PointerEvent): void {}
  onMouseDown(e: PointerEvent): void {}
  onMouseUp(e: PointerEvent): void {}

  Finish(operate: ModifyOperateState) {
    let result = {
      delData: this.m_delLineInfos,
      addData: this.m_addLineInfos,
    };
    this.m_context.SelectionManager.HighLighting = true;
    this.clear();
    if (operate == ModifyOperateState.complete) {
      return result;
    } else if (operate == ModifyOperateState.cancel) {
      return undefined;
    }
  }

  private createTempLineObject() {
    let geo = new THREE.BufferGeometry();
    let material = new THREE.LineBasicMaterial({ color: CommonColor.Aux });
    this.m_tempLineObject = new THREE.LineSegments(geo, material);
    this.m_tempLineObject.position.z = 10;
    this.m_tempLineObject.frustumCulled = false;
    this.m_context.SceneManager.AuxModel.add(this.m_tempLineObject);
  }
  private createTempHightLineObject() {
    let geo = new THREE.BufferGeometry();
    let material = new THREE.LineBasicMaterial({ color: CommonColor.Pick });
    this.m_tempHeightLineObject = new THREE.LineSegments(geo, material);
    this.m_tempHeightLineObject.position.z = 9;
    this.m_tempHeightLineObject.frustumCulled = false;
    this.m_context.SceneManager.AuxModel.add(this.m_tempHeightLineObject);
  }
  private createTempDarkLineObject() {
    let geo = new THREE.BufferGeometry();
    let material = new THREE.LineBasicMaterial({ color: CommonColor.Gray });
    this.m_tempDarkLineObject = new THREE.LineSegments(geo, material);
    this.m_tempDarkLineObject.position.z = 9;
    this.m_tempDarkLineObject.frustumCulled = false;
    this.m_context.SceneManager.AuxModel.add(this.m_tempDarkLineObject);
  }

  private computeCrossPoint() {
    if (this.m_curSelect == this.m_selects[0]) return;
    this.m_context.SelectionManager.HighLighting = false;
    if (
      this.m_curSelect &&
      this.m_preClickLinePoints &&
      this.m_curClickLinePoints
    ) {
      let result: any = this.m_context.ToolsManager.CheckLineIntersection(
        {
          points: [
            ...this.m_preClickLinePoints![0].toArray(),
            ...this.m_preClickLinePoints![1].toArray(),
          ],
        } as unknown as DbLine,
        {
          points: [
            ...this.m_curClickLinePoints![0].toArray(),
            ...this.m_curClickLinePoints![1].toArray(),
          ],
        } as unknown as DbLine
      );
      let line1Points = {
        start: this.m_preClickLinePoints[0].clone(),
        end: this.m_preClickLinePoints[1].clone(),
      };
      let line2Points = {
        start: this.m_curClickLinePoints[0].clone(),
        end: this.m_curClickLinePoints[1].clone(),
      };
      let line1ClickPoint = this.m_prePoint!.clone();
      let line2ClickPoint = this.m_curPoint.clone();
      let crossPoint = new THREE.Vector3();
      if (result.x && result.y) {
        crossPoint = new THREE.Vector3(result.x, result.y, 0);
      }

      let line1ClickPointToStartVector = new THREE.Vector3().subVectors(
        line1Points.start,
        line1ClickPoint
      );
      let line1ClickPointToCrossVector = new THREE.Vector3().subVectors(
        crossPoint,
        line1ClickPoint
      );
      let line1ClickPointToEndVector = new THREE.Vector3().subVectors(
        line1Points.end,
        line1ClickPoint
      );
      if (
        line1ClickPointToStartVector.dot(line1ClickPointToCrossVector.clone()) <
        0
      ) {
        let points = this.filterAssignPoints(
          this.m_preLinePoints,
          this.m_preClickLinePoints,
          true
        );
        this.m_addLine1Points = [line1Points.start, ...points];
      } else if (
        line1ClickPointToEndVector.dot(line1ClickPointToCrossVector.clone()) < 0
      ) {
        let points = this.filterAssignPoints(
          this.m_preLinePoints,
          this.m_preClickLinePoints,
          false
        );
        this.m_addLine1Points = [line1Points.end, ...points];
      }
      let line2ClickPointToStartVector = new THREE.Vector3().subVectors(
        line2Points.start,
        line2ClickPoint
      );
      let line2ClickPointToCrossVector = new THREE.Vector3().subVectors(
        crossPoint,
        line2ClickPoint
      );
      let line2ClickPointToEndVector = new THREE.Vector3().subVectors(
        line2Points.end,
        line2ClickPoint
      );

      if (
        line2ClickPointToStartVector.dot(line2ClickPointToCrossVector.clone()) <
        0
      ) {
        let points = this.filterAssignPoints(
          this.m_curLinePoints,
          this.m_curClickLinePoints,
          true
        );
        this.m_addLine2Points = [line2Points.start, ...points];
      } else if (
        line2ClickPointToEndVector.dot(line2ClickPointToCrossVector.clone()) < 0
      ) {
        let points = this.filterAssignPoints(
          this.m_curLinePoints,
          this.m_curClickLinePoints,
          false
        );
        this.m_addLine2Points = [line2Points.end, ...points];
      }
      let preClickLineP = this.m_addLine1Points[0];
      let curClickLineP = this.m_addLine2Points[0];
      let CircleArcInfo = this.fillet(
        preClickLineP,
        curClickLineP,
        crossPoint,
        this.m_radius
      );
      this.m_addLine3Points = [];
      if (CircleArcInfo) {
        this.m_addLine1Points.unshift(CircleArcInfo.tangencyPoint1);
        this.m_addLine2Points.unshift(CircleArcInfo.tangencyPoint2);
        let arcInfo = this.createArc(
          CircleArcInfo.center,
          this.m_radius,
          CircleArcInfo.tangencyPoint1,
          CircleArcInfo.tangencyPoint2
        );
        this.m_addLine3Points = [...arcInfo.arcPoints];
        this.m_circleArcOriginPoints = arcInfo.aPoints;
        this.m_circleAceInfo = {
          center: CircleArcInfo.center,
          radius: this.m_radius,
          start: CircleArcInfo.tangencyPoint1,
          end: CircleArcInfo.tangencyPoint2,
          clockwise: arcInfo.clockwise,
        };

        this.m_tempLineObject.geometry.setFromPoints([
          ...this.m_addLine1Points,
          ...this.m_addLine2Points,
          ...this.m_addLine3Points,
        ]);
      } else {
        this.m_addLine1Points.length = 0;
        this.m_addLine2Points.length = 0;
        this.m_addLine3Points.length = 0;
      }
    }
  }

  private endChamferHandle() {
    if (this.m_selects[0] == this.m_curSelect) return;
    if (!this.m_addLine1Points.length || !this.m_addLine2Points.length) {
      this.resetState();
      return;
    }
    let layer1 = this.m_context.ModelManager.GetLayerByIdColor(
      this.m_selects[0].layerId,
      this.m_selects[0].color!
    );
    let layer2 = this.m_context.ModelManager.GetLayerByIdColor(
      this.m_curSelect!.layerId,
      this.m_curSelect!.color!
    );
    this.removeDuplicates(this.m_addLine1Points);
    this.removeDuplicates(this.m_addLine2Points);

    this.m_context.ModelManager.DeleteEntity(
      this.m_selects[0],
      this.m_curSelect!
    );
    let addLines1 = [];
    if (this.m_firstSelectLayerLineTypeName == Linearity.Dash) {
      let breakPoints =
        this.m_context.ToolsManager.PassStartAndEndPointGetDashPoints(
          this.m_addLine1Points[0],
          this.m_addLine1Points[this.m_addLine1Points.length - 1]
        );

      addLines1 = this.m_context.ModelManager.AddCustomLines(
        layer1!.Name!,
        this.m_selects[0].color!,
        breakPoints,
        false
      );
    } else {
      addLines1 = this.m_context.ModelManager.AddCustomLines(
        layer1!.Name!,
        this.m_selects[0].color!,
        this.m_addLine1Points
      );
    }

    this.bindingOriginData(this.m_selects[0], addLines1);

    let addLines2 = [];

    if (this.m_curSelectLayerLineTypeName == Linearity.Dash) {
      let breakPoints =
        this.m_context.ToolsManager.PassStartAndEndPointGetDashPoints(
          this.m_addLine2Points[0],
          this.m_addLine2Points[this.m_addLine2Points.length - 1]
        );
      addLines2 = this.m_context.ModelManager.AddCustomLines(
        layer2!.Name!,
        this.m_curSelect!.color!,
        breakPoints,
        false
      );
    } else {
      addLines2 = this.m_context.ModelManager.AddCustomLines(
        layer2!.Name!,
        this.m_curSelect!.color!,
        this.m_addLine2Points
      );
    }

    this.bindingOriginData(this.m_curSelect!, addLines2);
    this.m_addLineInfos.push(...addLines1, ...addLines2);

    if (this.m_addLine3Points.length && this.m_circleAceInfo) {
      let addLines3 = [];
      if (this.m_firstSelectLayerLineTypeName == Linearity.Dash) {
        addLines3 = this.m_context.ModelManager.AddCustomLines(
          layer1!.Name!,
          this.m_selects[0].color!,
          this.m_circleArcOriginPoints,
          false
        );
      } else {
        addLines3 = this.m_context.ModelManager.AddCustomLines(
          layer1!.Name!,
          this.m_selects[0].color!,
          this.m_circleArcOriginPoints
        );
      }
      this.bindingOriginData(
        this.m_selects[0],
        addLines3,
        this.m_circleAceInfo
      );
      this.m_addLineInfos.push(...addLines3);
    }

    this.m_delLineInfos.push(this.m_selects[0], this.m_curSelect!);
    for (let i = 0; i < this.m_addLineInfos.length; i++) {
      for (let j = 0; j < this.m_delLineInfos.length; j++) {
        if (this.m_addLineInfos[i] === this.m_delLineInfos[j]) {
          this.m_addLineInfos.splice(i, 1);
          this.m_delLineInfos.splice(j, 1);
          i--;
          j--;
        }
      }
    }
    this.resetState();
  }
  private resetState() {
    this.m_selects = [];
    this.m_addLine1Points.length = 0;
    this.m_addLine2Points.length = 0;
    this.m_addLine3Points.length = 0;
    this.m_circleArcOriginPoints.length = 0;
    this.m_curSelect = undefined;
    this.m_circleAceInfo = undefined;
    this.m_context.SelectionManager.ClearSelected();
    this.m_tempLineObject.geometry.setFromPoints([]);
    this.m_tempHeightLineObject.geometry.setFromPoints([]);
    this.m_tempDarkLineObject.geometry.setFromPoints([]);
  }

  private clear() {
    this.m_context.ToolsManager.ObjectsClear([
      this.m_locationObject,
      this.m_tempLineObject!,
      this.m_tempHeightLineObject!,
      this.m_tempDarkLineObject!,
    ]);
  }

  private getObjPoints(points: number[]) {
    let p: THREE.Vector3[] = [];
    for (let i = 0; i < points.length; i += 3) {
      if (i == points.length - 3) break;
      let startP = new THREE.Vector3(points[i], points[i + 1], points[i + 2]);
      let endP = new THREE.Vector3(points[i + 3], points[i + 4], points[i + 5]);
      p.push(startP, endP);
    }
    return p;
  }
  private filterClickLine(
    lineData: THREE.Vector3[],
    clickPoint: THREE.Vector3
  ) {
    for (let i = 0; i < lineData.length; i += 2) {
      let start = lineData[i];
      let end = lineData[i + 1];
      let clickPToStartVectorL = start.clone().sub(clickPoint).length();
      let clickPToEndVectorL = end.clone().sub(clickPoint).length();
      if (
        Math.abs(
          start.distanceTo(end) - (clickPToStartVectorL + clickPToEndVectorL)
        ) < 0.001
      ) {
        return [start, end];
      }
    }
  }

  private filterAssignPoints(
    linePoints: THREE.Vector3[],
    clickLinePoints: THREE.Vector3[],
    isClockwise: boolean
  ) {
    let points: THREE.Vector3[] = [];
    let cv = new THREE.Vector3().subVectors(
      clickLinePoints[1],
      clickLinePoints[0]
    );
    if (isClockwise) {
      for (let i = 0; i < linePoints.length; i += 2) {
        let sPoint = linePoints[i];
        let ePoint = linePoints[i + 1];
        let v = ePoint.clone().sub(sPoint);
        if (cv.equals(v)) {
          return points;
        }
        points.push(sPoint, ePoint);
      }
    } else {
      for (let i = linePoints.length - 1; i >= 0; i -= 2) {
        let sPoint = linePoints[i - 1];
        let ePoint = linePoints[i];
        let v = ePoint.clone().sub(sPoint);
        if (cv.equals(v)) {
          return points.reverse();
        }
        points.push(ePoint, sPoint);
      }
    }

    return points;
  }

  private fillet(
    line1StartPoint: THREE.Vector3,
    line2StartPoint: THREE.Vector3,
    crossPoint: THREE.Vector3,
    radius: number
  ) {
    let line1Start = line1StartPoint;
    let line2Start = line2StartPoint;
    let line1 = line1Start.clone().sub(crossPoint);
    let line2 = line2Start.clone().sub(crossPoint);
    let angle = line1.angleTo(line2);
    angle = angle > Math.PI ? 2 * Math.PI - angle : angle;
    let length = radius / Math.sin(angle / 2);
    let lNormal = line1
      .clone()
      .normalize()
      .add(line2.clone().normalize())
      .normalize();
    let cPoint = lNormal.clone().multiplyScalar(length);
    cPoint.x = cPoint.x / 1000 + crossPoint.x;
    cPoint.y = cPoint.y / 1000 + crossPoint.y;
    // 切点1
    let length1 = radius / Math.tan(angle / 2);
    let tangencyVector1 = line1.clone().normalize().multiplyScalar(length1);
    let tangencyPoint1 = new THREE.Vector3(
      tangencyVector1.x / 1000 + crossPoint.x,
      tangencyVector1.y / 1000 + crossPoint.y,
      0
    );

    // 切点2
    let length2 = radius / Math.tan(angle / 2);
    if (
      length1 / 1000 > line1Start.distanceTo(crossPoint) ||
      length2 / 1000 > line2Start.distanceTo(crossPoint)
    ) {
      console.error("切点不在线上，在线上的话，半径太大");
      return undefined;
    }
    let tangencyVector2 = line2.clone().normalize().multiplyScalar(length2);
    let tangencyPoint2 = new THREE.Vector3(
      tangencyVector2.x / 1000 + crossPoint.x,
      tangencyVector2.y / 1000 + crossPoint.y,
      0
    );

    return {
      center: cPoint,
      tangencyPoint1: tangencyPoint1,
      tangencyPoint2: tangencyPoint2,
    };
  }

  private createArc(
    center: THREE.Vector3,
    radius: number,
    tangencyPoint1: THREE.Vector3,
    tangencyPoint2: THREE.Vector3,
    clockwise?: boolean
  ) {
    // 切点1和X轴的夹角
    let angle1 = tangencyPoint1
      .clone()
      .sub(center)
      .angleTo(new THREE.Vector3(1, 0, 0));
    // 切点2和X轴的夹角
    let angle2 = tangencyPoint2
      .clone()
      .sub(center)
      .angleTo(new THREE.Vector3(1, 0, 0));

    let vecTangencyPoint1 = tangencyPoint1.clone().sub(center);
    let vecTangencyPoint2 = tangencyPoint2.clone().sub(center);
    let dir = vecTangencyPoint1.clone().sub(vecTangencyPoint2).normalize();
    let dir1 = vecTangencyPoint1.clone().add(vecTangencyPoint2).normalize();
    let isclockwise = false;
    if (dir.x > 0 && dir.y > 0) {
      // 切点1在切点2的右上方
      if (dir1.x > 0 && dir1.y < 0) {
        isclockwise = true;
        if (tangencyPoint1.y <= center.y) {
          angle1 = 2 * Math.PI - angle1;
        }
        angle2 = 2 * Math.PI - angle2;
      } else if (dir1.x < 0 && dir1.y > 0) {
        isclockwise = false;
        if (tangencyPoint2.y <= center.y) {
          angle2 = 2 * Math.PI - angle2;
        }
      }
    } else if (dir.x > 0 && dir.y < 0) {
      //切点1在切点2的右下方
      if (dir1.x > 0 && dir1.y > 0) {
        isclockwise = false;
        if (tangencyPoint1.y <= center.y) {
          angle1 = 2 * Math.PI - angle1;
        }
      } else if (dir1.x < 0 && dir1.y < 0) {
        isclockwise = true;
        angle1 = 2 * Math.PI - angle1;
        if (tangencyPoint2.y <= center.y) {
          angle2 = 2 * Math.PI - angle2;
        }
      }
    } else if (dir.x < 0 && dir.y > 0) {
      //切点1在切点2的左上方
      if (dir1.x > 0 && dir1.y > 0) {
        isclockwise = true;
        if (tangencyPoint2.y <= center.y) {
          angle2 = 2 * Math.PI - angle2;
        }
      } else if (dir1.x < 0 && dir1.y < 0) {
        isclockwise = false;
        angle2 = 2 * Math.PI - angle2;
        if (tangencyPoint1.y <= center.y) {
          angle1 = 2 * Math.PI - angle1;
        }
      }
    } else if (dir.x < 0 && dir.y < 0) {
      //切点1在切点2的左下方
      if (dir1.x > 0 && dir1.y < 0) {
        isclockwise = false;
        angle1 = 2 * Math.PI - angle1;
        if (tangencyPoint2.y <= center.y) {
          angle2 = 2 * Math.PI - angle2;
        }
      } else if (dir1.x < 0 && dir1.y > 0) {
        isclockwise = true;
        if (tangencyPoint1.y <= center.y) {
          angle1 = 2 * Math.PI - angle1;
        }
      }
    }
    let arc = new THREE.ArcCurve(
      center.x,
      center.y,
      radius / 1000,
      angle1,
      angle2,
      clockwise === undefined ? isclockwise : clockwise
    );
    let aPoints = arc.getPoints(49).map((item) => {
      return new THREE.Vector3(item.x, item.y, 0);
    });
    let arcPoints: THREE.Vector3[] = [];
    for (let i = 0; i < aPoints.length - 1; i++) {
      arcPoints.push(aPoints[i], aPoints[i + 1]);
    }

    return {
      aPoints,
      arcPoints,
      clockwise: clockwise === undefined ? isclockwise : clockwise,
    };
  }
  private removeDuplicates(datas: THREE.Vector3[]) {
    for (let i = 0; i < datas.length - 1; i++) {
      let curData = datas[i];
      let nextData = datas[i + 1];
      if (curData.equals(nextData)) {
        datas.splice(i, 1);
        i--;
      }
    }
  }
  private bindingOriginData(
    originData: DbLine,
    newData: DbLine[],
    circleArcInfo?: CircleArcInfoUseFilleted
  ) {
    let oriData = originData.userData;
    if (!oriData) {
      oriData = originData;
    }
    for (let i = 0; i < newData.length; i++) {
      if (circleArcInfo) {
        newData[i].userData = {
          originData,
          circleArcInfo,
        };
      } else {
        newData[i].userData = {
          originData,
        };
      }
    }
  }
}

export { FilletedCornerModify };
